import { supabase } from "@/integrations/supabase/client";

export interface UserChapterProgress {
  userId: string;
  certificationId: string;
  chapterId: string;
  questionsCompleted: number;
  questionsTotal: number;
  lastScore: number;
  isWeak: boolean;
  updatedAt: string;
}

type Listener = () => void;
let _items: UserChapterProgress[] = [];
let _loaded = false;
let _currentUserId: string | null = null;
const listeners = new Set<Listener>();
const notify = () => listeners.forEach((fn) => fn());

async function loadFromDb(userId: string) {
  if (!userId) return;
  _currentUserId = userId;
  const { data, error } = await supabase
    .from("user_chapter_progress")
    .select("*")
    .eq("user_id", userId);
  if (error) {
    console.error("Failed to load user progress:", error);
    return;
  }
  _items = (data || []).map((r: any) => ({
    userId: r.user_id,
    certificationId: r.certification_id,
    chapterId: r.chapter_id,
    questionsCompleted: r.questions_completed,
    questionsTotal: r.questions_total,
    lastScore: r.last_score,
    isWeak: r.is_weak,
    updatedAt: r.updated_at,
  }));
  _loaded = true;
  notify();
}

export const userProgressStore = {
  get: () => _items,
  subscribe: (fn: Listener) => {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  },
  isLoaded: () => _loaded,
  
  async loadForUser(userId: string) {
    await loadFromDb(userId);
  },

  async upsert(progress: UserChapterProgress) {
    const { error } = await supabase
      .from("user_chapter_progress")
      .upsert({
        user_id: progress.userId,
        certification_id: progress.certificationId,
        chapter_id: progress.chapterId,
        questions_completed: progress.questionsCompleted,
        questions_total: progress.questionsTotal,
        last_score: progress.lastScore,
        is_weak: progress.isWeak,
      }, { onConflict: "user_id,chapter_id" });
    
    if (error) {
      console.error("Failed to upsert progress:", error);
      return;
    }
    
    // Update local state
    const idx = _items.findIndex(
      (p) => p.userId === progress.userId && p.chapterId === progress.chapterId
    );
    if (idx >= 0) {
      _items[idx] = progress;
    } else {
      _items.push(progress);
    }
    _items = [..._items];
    notify();
  },

  getProgress(userId: string, chapterId: string): UserChapterProgress | undefined {
    return _items.find((p) => p.userId === userId && p.chapterId === chapterId);
  },

  getCertProgress(userId: string, certificationId: string): UserChapterProgress[] {
    return _items.filter((p) => p.userId === userId && p.certificationId === certificationId);
  },

  reset() {
    _items = [];
    _loaded = false;
    _currentUserId = null;
    notify();
  },
};
